import {
  Footprints,
  Timer,
  RotateCcw,
  Clock3,
  Brain,
  ArrowUpDown,
  Clock,
  Gauge,
  Ruler,
  Activity,
  Target,
  Dumbbell,
  Move,
  RefreshCw,
  CircleDot,
} from 'lucide-react';
import type { ActivityType } from '@/types/activity';
import { cn } from '@/utils/cn';

const iconMap: Partial<Record<ActivityType, typeof Footprints>> = {
  'generic': Activity,
  '3min-test': Clock3,
  '6min-test': Clock,
  'tug': RotateCcw,
  '25ft-walk': Ruler,
  '10m-walk': Footprints,
  'static-10m-walk': Gauge,
  'dual-task-10m-walk': Brain,
  '4m-walk': Timer,
  'static-4m-walk': Gauge,
  'dual-task-4m-walk': Brain,
  'stair-climb': ArrowUpDown,
  'posturography': Target,
  'auto-logging': Activity,
  'mini-squat': Dumbbell,
  'semi-tandem-walk': Move,
  'lateral-dodging': Move,
  'tandem-pivot': RefreshCw,
  'timed': Timer,
  'tuning-fork': CircleDot,
  'tandem-walk': Footprints,
  'in-place-turns': RefreshCw,
  'pull-test': Target,
  'heel-raises': Dumbbell,
  'toe-tapping': Move,
  'choice-haptic-reaction-time': Brain,
};

const colorMap: Partial<Record<ActivityType, string>> = {
  'generic': 'bg-gray-100 text-gray-600',
  '3min-test': 'bg-orange-100 text-orange-600',
  '6min-test': 'bg-green-100 text-green-600',
  'tug': 'bg-purple-100 text-purple-600',
  '25ft-walk': 'bg-indigo-100 text-indigo-600',
  '10m-walk': 'bg-blue-100 text-blue-600',
  'static-10m-walk': 'bg-cyan-100 text-cyan-600',
  'dual-task-10m-walk': 'bg-pink-100 text-pink-600',
  '4m-walk': 'bg-teal-100 text-teal-600',
  'static-4m-walk': 'bg-cyan-100 text-cyan-600',
  'dual-task-4m-walk': 'bg-pink-100 text-pink-600',
  'stair-climb': 'bg-yellow-100 text-yellow-600',
  'posturography': 'bg-red-100 text-red-600',
  'auto-logging': 'bg-gray-100 text-gray-600',
  'mini-squat': 'bg-amber-100 text-amber-600',
  'semi-tandem-walk': 'bg-lime-100 text-lime-600',
  'lateral-dodging': 'bg-emerald-100 text-emerald-600',
  'tandem-pivot': 'bg-violet-100 text-violet-600',
  'timed': 'bg-slate-100 text-slate-600',
  'tuning-fork': 'bg-fuchsia-100 text-fuchsia-600',
  'tandem-walk': 'bg-sky-100 text-sky-600',
  'in-place-turns': 'bg-rose-100 text-rose-600',
  'pull-test': 'bg-stone-100 text-stone-600',
  'heel-raises': 'bg-amber-100 text-amber-700',
  'toe-tapping': 'bg-emerald-100 text-emerald-700',
  'choice-haptic-reaction-time': 'bg-indigo-100 text-indigo-700',
};

interface ActivityTypeIconProps {
  type: ActivityType;
  size?: 'sm' | 'md' | 'lg';
  showBackground?: boolean;
  className?: string;
}

const sizeStyles = {
  sm: { container: 'h-8 w-8', icon: 'h-4 w-4' },
  md: { container: 'h-10 w-10', icon: 'h-5 w-5' },
  lg: { container: 'h-12 w-12', icon: 'h-6 w-6' },
};

export function ActivityTypeIcon({
  type,
  size = 'md',
  showBackground = true,
  className,
}: ActivityTypeIconProps) {
  const Icon = iconMap[type] || Activity;
  const styles = sizeStyles[size];
  const colors = colorMap[type] || colorMap['generic'];

  if (!showBackground) {
    return <Icon className={cn(styles.icon, className)} />;
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-lg',
        styles.container,
        colors,
        className
      )}
    >
      <Icon className={styles.icon} />
    </div>
  );
}
